import type { User } from "@firebase/auth";
import { reportError } from "./backoffice.utils";
import { auth, firestore } from "./firebase";
import { AuthProvider } from "react-admin";
import { FirebaseAuthProvider } from "./providers/FirebaseAuthProvider";
import * as Sentry from "@sentry/react";
import intersection from "lodash/intersection";
import { CustomizationFlag, CustomizationFlagEnum } from "./model/customizationFlags";
import { schemas } from "./generated/serverClient";
import { drivingSchoolsProvider } from "./providers/drivingSchoolsProvider";
import { doc, getDoc } from "firebase/firestore";
import { getRedirectResult } from "firebase/auth";
import { errorReportProps } from "./sentry";
import { capitalize } from "./utils/string";

export let authProvider: AuthProvider;

type UserWithCallName = User & { callName: string | undefined };

export let currentUser: UserWithCallName | null | undefined;
/** Roles of the current user as received from auth token claims. */
export let currentUserRoles: Array<Role> = [];
/** All grants of the current user. */
export let grants: ReadonlySet<Grant>;
/** Might be set if the current user is staff or a driving school manager. */
export let restrictAccessToDrivingSchoolIds: Array<string> | undefined;

interface AuthState {
  currentUser: typeof currentUser;
  currentUserRoles: typeof currentUserRoles;
  grants: typeof grants;
  restrictAccessToDrivingSchoolIds: typeof restrictAccessToDrivingSchoolIds;
}

function toCallName(displayName: string | null): string | undefined {
  const firstName = displayName?.trim().split(/\s+/)[0];
  return firstName ? capitalize(firstName) : undefined;
}

export async function initAccessControl(): Promise<{ currentUser: typeof currentUser; grants: ReadonlySet<Grant> }> {
  authProvider = new FirebaseAuthProvider(auth);
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      console.info(`getRedirectResult(auth) => ${result.operationType} as user ${result.user.uid}`);
    }
  } catch (error) {
    console.info("Failed to get redirect result", error);
  }
  return new Promise<{ currentUser: typeof currentUser; grants: ReadonlySet<Grant> }>((resolveCurrentUser) =>
    auth.onIdTokenChanged(async (user) => {
      // delay the execution here, to prevent the "Could not reach Cloud Firestore backend." error ...
      const newAuthState = await new Promise<AuthState>((resolveAuthState) =>
        setTimeout(async () => {
          const currentUser: AuthState["currentUser"] = user
            ? Object.assign(user, { callName: toCallName(user.displayName) })
            : user;
          let currentUserRoles: AuthState["currentUserRoles"] = [];
          let grants = new Set<Grant>();
          let restrictAccessToDrivingSchoolIds: AuthState["restrictAccessToDrivingSchoolIds"] = undefined;
          try {
            if (user) {
              const {
                claims: { roles, entitledForDrivingSchools, instructorDrivingSchoolUid, autovioUserId },
              } = await user.getIdTokenResult();
              if (Array.isArray(roles)) {
                currentUserRoles = roles;
                if (roles.includes("admin")) {
                  // No access restrictions for admins.
                } else if (isAutovioStaff(roles)) {
                  // Autovio staff with entitledForDrivingSchools property are restricted to their driving schools.
                  // Autovio staff without entitledForDrivingSchools property can access all driving schools.
                  if (Array.isArray(entitledForDrivingSchools) && entitledForDrivingSchools.length > 0) {
                    restrictAccessToDrivingSchoolIds = entitledForDrivingSchools;
                  }
                } else if (roles.includes("drivingSchoolManager") || roles.includes("instructor")) {
                  if (instructorDrivingSchoolUid) {
                    restrictAccessToDrivingSchoolIds = [instructorDrivingSchoolUid as string];
                  } else {
                    throw new Error(`instructorDrivingSchoolUid is missing in claims of user ${user.uid}`);
                  }
                } else if (Array.isArray(entitledForDrivingSchools) && entitledForDrivingSchools.length > 0) {
                  restrictAccessToDrivingSchoolIds = entitledForDrivingSchools;
                } else {
                  throw new Error(
                    `Missing or invalid entitledForDrivingSchools property in claims of user ${user.uid}`,
                  );
                }
                let customizationFlags: Array<CustomizationFlag> = CustomizationFlagEnum.options;
                if (restrictAccessToDrivingSchoolIds?.length === 1) {
                  const { data: drivingSchool } = await drivingSchoolsProvider.getOne("drivingSchools", {
                    id: restrictAccessToDrivingSchoolIds[0],
                  });
                  customizationFlags = drivingSchool.customizations;
                }
                grants = grantsForRolesAndCustomizationFlags(roles, customizationFlags);
                if (currentUser && isDeveloper(currentUser)) {
                  grants = grants.union(developerGrants);
                }
                let { grants: userGrants } = (await getDoc(doc(firestore, `/users/${user.uid}`))).data() ?? {};
                if (Array.isArray(userGrants)) {
                  userGrants = userGrants.filter((it) => typeof it === "string");
                  grants = applyUserGrants(grants, userGrants, customizationFlags);
                }
                Object.assign(errorReportProps, {
                  currentUser: {
                    uid: user.uid,
                    name: user.displayName || "???",
                    roles: roles,
                    ...(userGrants ? { userGrants } : {}),
                  },
                });
              }
              Sentry.setUser({
                id: user.uid,
                email: user.email || user.providerData[0]?.email || "???",
                username: user.displayName || "???",
                autovioUserId: typeof autovioUserId === "string" ? autovioUserId : undefined,
              });
            }
          } catch (error) {
            reportError("Failed to initialize grants", error);
          } finally {
            resolveAuthState({ currentUser, currentUserRoles, grants, restrictAccessToDrivingSchoolIds });
          }
        }, 250),
      );
      ({ currentUser, currentUserRoles, grants, restrictAccessToDrivingSchoolIds } = newAuthState);
      resolveCurrentUser({ currentUser, grants });
    }),
  );
}

const metaGrants: Record<string, ReadonlySet<Grant>> = {
  "DrivingLesson:bookOnBehalfOfInstructor": new Set<Grant>([
    "DrivingLesson:create",
    "DrivingSchool:vehicle:list",
    "Instructor:list",
    "Student:list",
  ]),
  manageDrivingSchoolAutoForm: new Set<Grant>([
    "DrivingSchool:AutoForm:create",
    "DrivingSchool:AutoForm:edit",
    "DrivingSchool:AutoForm:delete",
    "DrivingSchool:AutoForm:list",
  ]),
  manageFleet: new Set<Grant>([
    "Vehicle:create",
    "Vehicle:delete",
    "Vehicle:edit",
    "Vehicle:viewDeleted",
    "VehicleEvent:create",
    "VehicleEvent:edit",
    "VehicleEvent:delete",
  ]),
  manageBkfCourses: new Set<Grant>([
    "Course:create",
    "Course:list",
    "CourseLesson:delete",
    "CourseLesson:getAttendance",
    "CourseLesson:setAttendance",
    "CourseTemplate:create",
    "CourseTemplate:delete",
    "CourseTemplate:edit",
    "CourseTemplate:list",
    "Room:create",
    "Room:edit",
    "Room:delete",
    "Timetable:create",
    "Timetable:delete",
    "Timetable:edit",
    "Timetable:list",
  ]),
  manageTheoryLessons: new Set<Grant>([
    "Branch:list",
    "TheoryCourse:create",
    "TheoryLesson:create",
    "TheoryLesson:edit",
    "addStudentToTheoryLesson",
    "removeStudentFromTheoryLesson",
    "theoryCoursesCreate",
  ]),
  manageUsers: new Set<Grant>(["Employee:create", "Employee:list", "Instructor:assignBranches", "manageUsers"]),
  createCardReaderPayment: new Set<Grant>(["Invoice:listPerStudent", "Invoice:createCardReaderPayment"]),
  createCashPayment: new Set<Grant>(["Invoice:listPerStudent", "Invoice:createCashPayment"]),
  createExternalBankTransfer: new Set<Grant>(["Invoice:listPerStudent", "Invoice:createExternalBankTransfer"]),
  createManualInvoice: new Set<Grant>(["Invoice:listPerStudent", "Invoice:createManual"]),
  examCreate: new Set<Grant>(["Exam:list", "Exam:create", "Exam:historical:create"]),
  addExamInThePast: new Set<Grant>(["Exam:historical:create"]),
  calendarViewEventsOfOtherInstructors: new Set<Grant>(["Instructor:list", "Student:list"]),
};

const deniedMetaGrants: Record<string, ReadonlySet<Grant>> = {
  "!createCardReaderPayment": new Set<Grant>(["Invoice:createCardReaderPayment"]),
  "!createCashPayment": new Set<Grant>(["Invoice:createCashPayment"]),
  "!createExternalBankTransfer": new Set<Grant>(["Invoice:createExternalBankTransfer"]),
  "!createManualInvoice": new Set<Grant>(["Invoice:createManual"]),
  "!examCreate": new Set<Grant>(["Exam:create", "Exam:historical:create"]),
};

const requiredCustomizationFlagsForGrants: Partial<Record<CustomizationFlag, Set<Grant>>> = {
  canAddGiftedCredits: new Set<Grant>(["giftCredits:add"]),
};

export function applyUserGrants(
  grants: Set<Grant>,
  userGrants: ReadonlyArray<string>,
  customizationFlags: Array<CustomizationFlag>,
): Set<Grant> {
  // Round 1: handle meta grants ...
  for (const key of userGrants) {
    const additionalGrants = metaGrants[key];
    if (additionalGrants) {
      if (isGrantMissingCustomizationFlag(key as Grant, customizationFlags)) {
        continue;
      }
      grants = grants.union(additionalGrants);
    } else if (key.startsWith("!")) {
      const deniedGrants = deniedMetaGrants[key] ?? metaGrants[key.substring(1)];
      if (deniedGrants) {
        grants = grants.difference(deniedGrants);
      }
    }
  }
  // Round 2: handle normal grants ...
  for (const key of userGrants) {
    if (key in metaGrants) {
      continue;
    } else if (key.startsWith("!")) {
      const key_ = key.substring(1) as Grant;
      if (key_ in metaGrants) {
        continue;
      }
      grants.delete(key_);
    } else {
      if (isGrantMissingCustomizationFlag(key as Grant, customizationFlags)) {
        continue;
      }
      grants.add(key as Grant);
    }
  }
  return grants;
}

function isGrantMissingCustomizationFlag(
  grant: Grant,
  drivingSchoolCustomizationFlags: Array<CustomizationFlag>,
): boolean {
  const [requiredCustomizationFlag] =
    Object.entries(requiredCustomizationFlagsForGrants).find((entry): entry is [CustomizationFlag, Set<Grant>] =>
      entry[1].has(grant),
    ) ?? [];
  return !!requiredCustomizationFlag && !drivingSchoolCustomizationFlags.includes(requiredCustomizationFlag);
}

export function isAutovioStaff(roles: Array<Role>): boolean {
  return intersection(roles, ["admin", "partnerSuccess", "studentSales"]).length > 0;
}

function isDeveloper(user?: User | null): boolean {
  if (!user) {
    return false;
  }
  const googleData = user.providerData.find((it) => it.providerId === "google.com");
  const email = googleData?.email ?? user.email ?? "";
  return [
    "amir@autovio.de",
    "michael@autovio.de",
    "tom.wallroth@autovio.de",
    "philipp@autovio.de",
    "motasemebrahim.m@gmail.com",
    "tom@autovio.de",
  ].includes(email);
}

const studentSalesGrants: ReadonlyArray<Grant> = [
  "viewDATEV",
  "ServiceEntry:list",
  ...metaGrants.manageTheoryLessons,
  "BookedQuotes:list",
  "Document:viewSignatureFolder",
  "DrivingLesson:bookWithoutInvite",
  "DrivingLesson:create",
  "DrivingLicenseApplication:edit",
  "DrivingSchool:vehicle:list",
  "DrivingSchoolLabel:list",
  "Employee:list",
  "Exam:bookWithoutInvite",
  "Exam:create",
  "Exam:list",
  "ExamLocation:list",
  "ExternalTrainingProgress:manage",
  "Fleet:list",
  "FundingInstructions:create",
  "FundingInstructions:view",
  "giftCredits:add",
  "giftCredits:listOptions",
  "Instructor:list",
  "Invoice:createManual",
  "Invoice:listPerStudent",
  "Invoice:manualRefund",
  "Invoice:refund",
  "Quote:book",
  "Student:assignLabel",
  "Student:assignInstructor",
  "Student:cancelBookedTraining",
  "Student:list",
  "SubscriptionPackage:list",
  "SignatureRequest:create",
  "Exam:historical:create",
  "addStudentToTheoryLesson",
  "bookAppointments",
  "calendarEventEdit",
  "changeBundle",
  "changeStatusOfStudent",
  "editStudentPaymentStrategy",
  "editStudentPersonalData",
  "removeStudentFromTheoryLesson",
  "resetPassword",
  "resetTheoryLearning",
  "retryPayments",
  "sendEmailSammelquittung",
  "theoryCoursesCreate",
  "viewAppliedReferralCodes",
  "viewBackendAccounts",
  "viewBundles",
  "viewCalendarEventEditHistory",
  "viewDrivingSchoolBusinessIntelligenceData",
  "viewHubspot",
  "viewPerformanceOverview",
  "viewPerformanceOverviewExcelExport",
  "viewPromoCodes",
  "viewStudentBalance",
  "viewStudentCredits",
  "viewStudentPayments",
  "updateInvoicePDF",
];

const partnerSuccessGrants: ReadonlyArray<Grant> = [
  ...studentSalesGrants,
  ...metaGrants.manageFleet,
  "Instructor:assignBranches",
  "Instructor:editAvailabilities",
  "viewAutovioBusinessIntelligenceData",
  "viewFinanceReports",
  "viewPayoutInformation",
  "viewManualRefunds",
  "viewPerformanceOverviewTakeRate",
  "viewPerformanceOverviewCompensation",
  "updateInvoicePDF",
];

const drivingSchoolAccountantGrants: ReadonlyArray<Grant> = [
  "viewDATEV",
  "ServiceEntry:list",
  "Invoice:listPerStudent",
  "Instructor:list",
  "Student:list",
  "sendDunningNotice",
  "sendEmailSammelquittung",
  "viewB2bInvoices",
  "viewBackendAccounts",
  "viewCalendarEventEditHistory",
  "viewDrivingSchoolBusinessIntelligenceData",
  "viewDrivingSchoolInvoiceCustomers",
  "viewFinanceReports",
  "viewPayoutInformation",
  "viewPerformanceOverview",
  "viewPerformanceOverviewColumnSelection",
  "viewPerformanceOverviewCompensation",
  "viewPerformanceOverviewDownloadReport",
  "viewPerformanceOverviewExcelExport",
  "viewPerformanceOverviewLegacyData",
  "viewPerformanceOverviewTakeRate",
  "viewStripe",
  "viewStudentBalance",
  "viewStudentCredits",
  "viewStudentPayments",
];

const getDrivingSchoolCustomerServiceGrants: (customizationFlags: Array<CustomizationFlag>) => ReadonlyArray<Grant> = (
  customizationFlags,
) =>
  [
    ...metaGrants.manageTheoryLessons,
    "BookedQuotes:list",
    "Branch:list",
    "Contract:generatePdf",
    "Document:viewSignatureFolder",
    "DrivingLesson:create",
    "DrivingLicenseApplication:edit",
    "DrivingSchool:vehicle:list",
    "DrivingSchoolLabel:create",
    "DrivingSchoolLabel:edit",
    "DrivingSchoolLabel:delete",
    "DrivingSchoolLabel:list",
    "OtherEvent:category:create",
    "OtherEvent:category:delete",
    "OtherEvent:category:edit",
    "OtherEvent:category:list",
    "Employee:list",
    "Exam:create",
    "Exam:list",
    "ExamLocation:create",
    "ExamLocation:edit",
    "ExamLocation:delete",
    "ExamLocation:list",
    "ExternalTrainingProgress:manage",
    "Fleet:list",
    ...(customizationFlags.includes("canAddGiftedCredits") ? (["giftCredits:add"] satisfies Array<Grant>) : []),
    ...(customizationFlags.includes("canManageStripeIBAN")
      ? (["FundingInstructions:create", "FundingInstructions:view"] satisfies Array<Grant>)
      : []),
    "Instructor:list",
    "Invoice:listPerStudent",
    "Note:create",
    "Note:delete",
    "Quote:create",
    "Quote:book",
    "ServiceType:create",
    "ServiceType:edit",
    "ServiceType:delete",
    "ServiceEntry:list",
    "ServiceType:list",
    "Student:assignInstructor",
    "Student:assignLabel",
    "Student:list",
    ...(customizationFlags.includes("canCreateStudents")
      ? (["Student:create", "Student:ShowCreateButton"] satisfies Array<Grant>)
      : []),
    "Vehicle:show",
    "VehicleEvent:create",
    "VehicleEvent:edit",
    "VehicleEvent:delete",
    "addStudentToTheoryLesson",
    "asfCoursesCreate",
    "calendarEventEdit",
    "changeBundle",
    "changeStatusOfStudent",
    "editStudentPaymentStrategy",
    "editStudentPersonalData",
    "Student:cancelBookedTraining",
    "removeStudentFromTheoryLesson",
    "resetPassword",
    ...(customizationFlags.includes("theoryLearning") ? (["resetTheoryLearning"] satisfies Array<Grant>) : []),
    "sendEmailSammelquittung",
    "theoryCoursesCreate",
    "viewPrice",
    "Exam:historical:create",
    ...(customizationFlags.includes("paymentAndInvoicing")
      ? (["viewDrivingSchoolInvoiceCustomers", "editStudentBudget"] satisfies Array<Grant>)
      : []),
  ] satisfies ReadonlyArray<Grant>;

const getDrivingSchoolInstructorGrants: (customizationFlags: Array<CustomizationFlag>) => ReadonlyArray<Grant> = (
  customizationFlags,
) => [
  "CourseLesson:getAttendance",
  "CourseLesson:setAttendance",
  "DrivingSchoolLabel:list",
  "Exam:list",
  "ExamLocation:list",
  "ExternalTrainingProgress:manage",
  "Fleet:list",
  "Student:assignLabel",
  "SignatureRequest:create",
  "removeStudentFromTheoryLesson",
  ...(customizationFlags.includes("theoryLearning") ? (["resetTheoryLearning"] satisfies Array<Grant>) : []),
];

const getDrivingSchoolManagerGrants: (customizationFlags: Array<CustomizationFlag>) => ReadonlyArray<Grant> = (
  customizationFlags,
) => [
  ...getDrivingSchoolInstructorGrants(customizationFlags),
  ...getDrivingSchoolCustomerServiceGrants(customizationFlags),
  ...drivingSchoolAccountantGrants,
  ...drivingSchoolAnalystGrants,
  ...metaGrants.manageTheoryLessons,
  ...metaGrants.manageFleet,
  ...(customizationFlags.includes("bkfCourses") ? metaGrants.manageBkfCourses : []),
  "BookedQuotes:list",
  "Course:list",
  "Branch:edit",
  "Branch:list",
  "Document:viewSignatureFolder",
  "DrivingLesson:create",
  "DrivingLicenseApplication:edit",
  "DrivingSchool:vehicle:list",
  "DrivingSchoolLabel:create",
  "DrivingSchoolLabel:edit",
  "DrivingSchoolLabel:delete",
  "Employee:create",
  "Employee:list",
  "OtherEvent:category:create",
  "OtherEvent:category:delete",
  "OtherEvent:category:edit",
  "OtherEvent:category:list",
  "Exam:create",
  "ExamLocation:create",
  "ExamLocation:edit",
  "ExamLocation:delete",
  ...(customizationFlags.includes("canAddGiftedCredits") ? (["giftCredits:add"] satisfies Array<Grant>) : []),
  ...(customizationFlags.includes("canManageStripeIBAN")
    ? (["FundingInstructions:create", "FundingInstructions:view"] satisfies Array<Grant>)
    : []),
  "Instructor:assignBranches",
  "Instructor:editAvailabilities",
  "Instructor:list",
  "Invoice:createManual",
  "Invoice:listPerStudent",
  "Note:delete",
  "Quote:create",
  "ServiceType:create",
  "ServiceType:edit",
  "ServiceType:delete",
  "ServiceType:list",
  "Student:assignInstructor",
  "Student:list",
  ...(customizationFlags.includes("canCreateStudents")
    ? (["Student:create", "Student:ShowCreateButton"] satisfies Array<Grant>)
    : []),
  "SignatureRequest:create",
  "addStudentToTheoryLesson",
  "Exam:historical:create",
  "asfCoursesCreate",
  "calendarEventEdit",
  "changePrice",
  "viewPrice",
  "changeStatusOfStudent",
  "editStudentPaymentStrategy",
  "editStudentPersonalData",
  "removeStudentFromTheoryLesson",
  "resetPassword",
  "sendEmailSammelquittung",
  "theoryCoursesCreate",
  "viewAreas",
  "viewBundles",
  "viewDrivingSchoolBusinessIntelligenceData",
  ...(customizationFlags.includes("paymentAndInvoicing")
    ? ([
        "editStudentBudget",
        "viewB2bInvoices",
        "viewDrivingSchoolInvoiceCustomers",
        "viewFinanceReports",
        "viewPerformanceOverview",
        "viewPerformanceOverviewColumnSelection",
        "viewPerformanceOverviewDownloadReport",
        "viewPerformanceOverviewExcelExport",
        "viewPayoutInformation",
        "viewManualRefunds",
      ] satisfies Array<Grant>)
    : []),
];

const developerGrants: ReadonlySet<Grant> = new Set<Grant>([
  "developer",
  "viewFirestore",
  "copyIdTokenToClipboard",
  "previewStudentDetails2",
  "viewCustomizationFlagsUsage",
  "viewPerformanceOverviewLegacyData",
  "Student:getFilledOutForm",
  "Student:create",
  "Student:ShowCreateButton",
  "editStudentAdjustPrepaidCredits",
]);

const drivingSchoolAnalystGrants: ReadonlyArray<Grant> = ["viewDrivingSchoolBusinessIntelligenceData"];

const adminGrants: { [k in Grant]: boolean } = {
  viewDATEV: true,
  editDATEVConfig: true,
  // a small hack to make typescript remind us when we forget to add a grant to the admin role.
  // there is AFAIK no way to make typescript force an array to have all values of an enum, but we can at least
  // create an object that has all values of the enum as keys and then use that object to construct the admin grants array.
  "CustomCard:create": true,
  "CustomCard:edit": true,
  "CustomCard:delete": true,
  "CustomCard:list": true,
  "BookedQuotes:list": true,
  "Branch:create": true,
  "Branch:edit": true,
  "Branch:list": true,
  "CalendarEvent:historical:delete": true,
  "CalendarEvent:triggerPayment": true,
  "Contract:generatePdf": true,
  "Course:create": true,
  "Course:list": true,
  "CourseLesson:delete": true,
  "CourseLesson:getAttendance": true,
  "CourseLesson:setAttendance": true,
  "CourseTemplate:create": true,
  "CourseTemplate:delete": true,
  "CourseTemplate:edit": true,
  "CourseTemplate:list": true,
  "Document:viewSignatureFolder": true,
  "DrivingLesson:bookWithoutInvite": true,
  "DrivingLesson:create": true,
  "DrivingLesson:historical:create": true,
  "DrivingLicenseApplication:edit": true,
  "DrivingSchool:AutoForm:create": true,
  "DrivingSchool:AutoForm:delete": true,
  "DrivingSchool:AutoForm:edit": true,
  "DrivingSchool:AutoForm:list": true,
  "DrivingSchool:vehicle:list": true,
  "DrivingSchoolLabel:create": true,
  "DrivingSchoolLabel:edit": true,
  "DrivingSchoolLabel:delete": true,
  "DrivingSchoolLabel:list": true,
  "OtherEvent:category:create": true,
  "OtherEvent:category:delete": true,
  "OtherEvent:category:edit": true,
  "OtherEvent:category:list": true,
  "Employee:create": true,
  "Employee:list": true,
  "Exam:bookWithoutInvite": true,
  "Exam:historical:create": true,
  "Exam:create": true,
  "Exam:list": true,
  "ExamAuthorityFee:create": true,
  "ExamAuthorityFee:edit": true,
  "ExamAuthorityFee:list": true,
  "ExamLocation:create": true,
  "ExamLocation:edit": true,
  "ExamLocation:delete": true,
  "ExamLocation:list": true,
  "ExternalTrainingProgress:manage": true,
  "Fleet:list": true,
  "FundingInstructions:create": true,
  "FundingInstructions:view": true,
  "giftCredits:add": true,
  "giftCredits:listOptions": true,
  "Instructor:assignBranches": true,
  "Instructor:editAvailabilities": true,
  "Instructor:list": true,
  "Invoice:createCardReaderPayment": true,
  "Invoice:createCashPayment": true,
  "Invoice:createExternalBankTransfer": true,
  "Invoice:createManual": true,
  "Invoice:listPerStudent": true,
  "Invoice:manualRefund": true,
  "Invoice:refund": true,
  "Note:create": true,
  "Note:delete": true,
  "Note:viewDeleted": true,
  "Post:delete": true,
  "Post:viewDeleted": true,
  "PrepaidCredits:refund": true,
  "Quote:book": true,
  "Quote:create": true,
  "Room:create": true,
  "Room:edit": true,
  "Room:delete": true,
  "Scheduler:view": true,
  "ServiceType:create": true,
  "ServiceType:edit": true,
  "ServiceType:delete": true,
  "ServiceEntry:list": true,
  "ServiceType:list": true,
  "Student:assignInstructor": true,
  "Student:assignLabel": true,
  "Student:cancelBookedTraining": true,
  "Student:create": true,
  "Student:getFilledOutForm": true,
  "Student:list": true,
  "Student:ShowCreateButton": true,
  "TheoryCourse:create": true,
  "TheoryLesson:create": true,
  "TheoryLesson:edit": true,
  "TheoryLesson:historical:create": true,
  "SubscriptionPackage:create": true,
  "SubscriptionPackage:delete": true,
  "SubscriptionPackage:edit": true,
  "SubscriptionPackage:list": true,
  "SignatureRequest:create": true,
  "SignatureRequest:deleteSignedAusbildungsnachweis": true,
  "Timetable:create": true,
  "Timetable:delete": true,
  "Timetable:edit": true,
  "Timetable:list": true,
  "Vehicle:create": true,
  "Vehicle:delete": true,
  "Vehicle:edit": true,
  "Vehicle:show": true,
  "Vehicle:viewDeleted": true,
  "VehicleEvent:create": true,
  "VehicleEvent:edit": true,
  "VehicleEvent:delete": true,
  addStudentToTheoryLesson: true,
  admin: true,
  asfCoursesCreate: true,
  blockStudent: true,
  bookAppointments: true,
  calendarEventEdit: true,
  changeBundle: true,
  changePrice: true,
  changeStatusOfStudent: true,
  changeStatusOfStudentWithOutstandingPayments: true,
  copyIdTokenToClipboard: false,
  developer: false,
  editBundles: true,
  editStudentApplicationFeePercentage: true,
  editStudentPaymentStrategy: true,
  editStudentBudget: true,
  editStudentPersonalData: true,
  editHubspotContactId: true,
  editStudentAdjustPrepaidCredits: false, // only for developers
  hubspotSync: true,
  manageDrivingLicenseAuthorities: true,
  manageUsers: true,
  overrideFeatureToggles: true,
  previewStudentDetails2: false,
  removeStudentFromTheoryLesson: true,
  resetPassword: true,
  resetTheoryLearning: true,
  retryPayments: true,
  theoryCoursesCreate: true,
  updateInvoicePDF: true,
  sendDunningNotice: true,
  sendEmailSammelquittung: true,
  viewADAS: true,
  viewAdditionalEventDetails: true,
  viewAreas: true,
  viewAppliedReferralCodes: true,
  viewAutovioBusinessIntelligenceData: true,
  viewAutovioEmployees: true,
  viewB2bInvoices: true,
  viewBackendAccounts: true,
  viewBundles: true,
  viewCalendarEventEditHistory: true,
  viewCustomizationFlagsUsage: false,
  viewDrivingSchoolBusinessIntelligenceData: true,
  viewDunning: true,
  viewHubspot: true,
  viewFinanceReports: true,
  viewFirestore: false,
  viewInAppNotifications: true,
  viewInvoiceCustomers: true,
  viewDrivingSchoolInvoiceCustomers: true,
  viewManualRefunds: true,
  viewPayoutInformation: true,
  viewPerformanceOverview: true,
  viewPerformanceOverviewCompensation: true,
  viewPerformanceOverviewTakeRate: true,
  viewPerformanceOverviewColumnSelection: true,
  viewPerformanceOverviewDownloadReport: true,
  viewPerformanceOverviewExcelExport: true,
  viewPerformanceOverviewLegacyData: false,
  viewProducts: true,
  viewPromoCodes: true,
  viewStripe: true,
  viewStudentBalance: true,
  viewStudentCredits: true,
  viewStudentPayments: true,
  viewPrice: true,
  viewTrainings: true,
  viewUsersToBeDeleted: true,
  webinar: true,
};

export function grantsForRolesAndCustomizationFlags(
  roles: Array<Role>,
  customizationFlags: Array<CustomizationFlag>,
): Set<Grant> {
  return new Set(_grantsForRolesAndCustomizationFlags(roles, customizationFlags));
}

function _grantsForRolesAndCustomizationFlags(
  roles: Array<Role>,
  customizationFlags: Array<CustomizationFlag>,
): Array<Grant> {
  if (roles.includes("admin")) {
    return Object.entries(adminGrants)
      .filter(([_, v]) => v)
      .map(([k, _]) => k) as Grant[];
  }
  let grants: Grant[] = [];
  if (roles.includes("instructor")) {
    grants = grants.concat(getDrivingSchoolInstructorGrants(customizationFlags));
  }
  if (roles.includes("drivingSchoolAccountant")) {
    grants = grants.concat(drivingSchoolAccountantGrants);
  }
  if (roles.includes("drivingSchoolAnalyst")) {
    grants = grants.concat(drivingSchoolAnalystGrants);
  }
  if (roles.includes("drivingSchoolCustomerService")) {
    grants = grants.concat(
      getDrivingSchoolCustomerServiceGrants(customizationFlags),
      "SignatureRequest:deleteSignedAusbildungsnachweis",
    );
  }
  if (roles.includes("drivingSchoolManager")) {
    grants = grants.concat(getDrivingSchoolManagerGrants(customizationFlags));
  }
  if (roles.includes("partnerSuccess")) {
    grants = grants.concat(partnerSuccessGrants);
  }
  if (roles.includes("studentSales")) {
    grants = grants.concat(studentSalesGrants);
  }
  return grants;
}

/** User-level grant toggle names extracted from the server's UpdateUserGrantsDto schema. */
const userGrantToggleNames: ReadonlyArray<string> = schemas.UpdateUserGrantsDto.shape.delta.element.options.filter(
  (item: string) => !item.startsWith("!"),
);

/**
 * Returns default user grant toggle values based on the user's roles.
 * For each toggle grant that would be implicitly granted by the role,
 * the corresponding key is set to `true`.
 */
export function getDefaultUserGrantToggles(
  roles: Role[],
  customizationFlags: CustomizationFlag[],
): Record<string, boolean> {
  const roleGrants = grantsForRolesAndCustomizationFlags(roles, customizationFlags);
  const defaults: Record<string, boolean> = {};
  for (const name of userGrantToggleNames) {
    const subGrants = metaGrants[name];
    if (subGrants) {
      // Meta-grant: default to true if all sub-grants are covered by role grants
      if ([...subGrants].every((g) => roleGrants.has(g))) {
        defaults[name] = true;
      }
    } else if (roleGrants.has(name as Grant)) {
      // Direct grant: default to true if it's in role grants
      defaults[name] = true;
    }
  }
  return defaults;
}
